import { inject, Injectable } from '@angular/core';
import { type Observable, ReplaySubject, takeUntil } from 'rxjs';

import {
  CHAT_CONTEXT_PRIORITY,
  type ChatContext,
  type ChatContextProvider,
  type ChatContextSessionHandle,
  type ChatTableContext,
  createChatContextSessionStack,
} from '@blaze-dashboard/ai-chat-domain';
import { TableChangesService } from '@blaze-shared/datasets-data-access';

export interface TableEditorChatContextSession {
  readonly changedTableIds$: Observable<string>;
  tableRenamed(tableName: string): void;
  closed(): void;
}

@Injectable({ providedIn: 'root' })
export class TableEditorChatContextProvider implements ChatContextProvider {
  private readonly tableChangesService = inject(TableChangesService);
  private readonly stack = createChatContextSessionStack<string, ChatTableContext>(toTableContext);
  readonly priority = CHAT_CONTEXT_PRIORITY.overlayArea;
  readonly context$: Observable<ChatContext | null> = this.stack.context$;

  opened(tableName: string): TableEditorChatContextSession {
    return new TableEditorSession(
      this.stack.open(tableName),
      this.tableChangesService.observeTablesChanged(),
    );
  }
}

class TableEditorSession implements TableEditorChatContextSession {
  private readonly closed$ = new ReplaySubject<void>(1);
  readonly changedTableIds$: Observable<string>;

  constructor(
    private readonly handle: ChatContextSessionHandle<string>,
    changedTableIds$: Observable<string>,
  ) {
    this.changedTableIds$ = changedTableIds$.pipe(takeUntil(this.closed$));
  }

  tableRenamed(tableName: string): void {
    this.handle.state$.next(tableName);
  }

  closed(): void {
    this.handle.close();
    this.closed$.next();
    this.closed$.complete();
  }
}

function toTableContext(tableName: string): ChatTableContext {
  return { $type: 'table', tableName };
}
